import type { ReactNode } from 'react';
import { Minus } from 'lucide-react';
import { Card, StatTile, formatEuro, formatMonat } from '@elbklar/ui';

import type { ProjectionSeries } from '@/types';

interface VergleichsLeisteProps {
  basis: ProjectionSeries;
  szenario?: ProjectionSeries | null;
  horizont: number;
  anzahlAusgewaehlt: number;
  summeAusgewaehlt?: string;
}

/**
 * Die Ausgangslage — dauerhaft sichtbar, auch wenn nichts ausgewaehlt ist.
 *
 * Vorher zeigte die Seite nur ein Delta ("kostet dich 2 Monate"), sobald man
 * etwas anhakte. Ohne Bezugswert ist ein Delta aber keine Entscheidungshilfe:
 * zwei verlorene Monate sind harmlos, wenn zwanzig da sind, und existenziell,
 * wenn drei da sind. Deshalb steht die Basis links und bleibt stehen.
 *
 * Gemessen wird in Datum und Euro, nicht in Monaten — "reicht bis September
 * 2027" ist fuer eine Entscheidung greifbarer als "13 Monate".
 */
export function VergleichsLeiste({
  basis,
  szenario,
  horizont,
  anzahlAusgewaehlt,
  summeAusgewaehlt,
}: VergleichsLeisteProps) {
  const aktiv = Boolean(szenario) && anzahlAusgewaehlt > 0;

  return (
    <Card flush className="overflow-hidden">
      <div className="grid grid-cols-1 divide-y divide-border sm:grid-cols-2 sm:divide-x sm:divide-y-0">
        <Spalte titel="Ausgangslage" unterzeile="ohne Entwürfe">
          <StatTile label="Geld reicht bis" value={reichtBis(basis, horizont)} />
          <StatTile
            label="Tiefpunkt"
            value={formatEuro(basis.min_balance)}
            tone={Number(basis.min_balance) < 0 ? 'danger' : undefined}
            hint={basis.min_balance_label ? formatMonat(basis.min_balance_label) : undefined}
          />
        </Spalte>

        <Spalte
          titel={aktiv ? `Mit Auswahl (${anzahlAusgewaehlt})` : 'Mit Auswahl'}
          unterzeile={aktiv && summeAusgewaehlt ? `Summe ${formatEuro(summeAusgewaehlt)}` : 'nichts ausgewählt'}
        >
          {aktiv && szenario ? (
            <>
              <StatTile
                label="Geld reicht bis"
                value={reichtBis(szenario, horizont)}
                delta={monatsDelta(basis.runway_months, szenario.runway_months)}
              />
              <StatTile
                label="Tiefpunkt"
                value={formatEuro(szenario.min_balance)}
                tone={Number(szenario.min_balance) < 0 ? 'danger' : undefined}
                delta={euroDelta(basis.min_balance, szenario.min_balance)}
                hint={szenario.min_balance_label ? formatMonat(szenario.min_balance_label) : undefined}
              />
            </>
          ) : (
            <div className="flex min-h-26 items-center gap-2 text-muted-foreground sm:col-span-2">
              <Minus size={16} strokeWidth={1.75} aria-hidden />
              Entwürfe anhaken, um zu vergleichen
            </div>
          )}
        </Spalte>
      </div>
    </Card>
  );
}

function Spalte({ titel, unterzeile, children }: { titel: string; unterzeile: string; children: ReactNode }) {
  return (
    <div className="p-5">
      <div className="mb-3">
        <div className="t-label text-muted-foreground">{titel}</div>
        <div className="t-small text-muted-foreground">{unterzeile}</div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

type Delta = { text: string; good: boolean } | undefined;

/** „September 2027“ oder „> 24 Monate“. */
function reichtBis(serie: ProjectionSeries, horizont: number): string {
  if (serie.runway_date) {
    return formatMonat(serie.runway_date);
  }
  return serie.horizon_exceeded ? `> ${horizont} Monate` : '–';
}

/**
 * Verschiebung des Zeitpunkts, an dem das Geld ausgeht.
 *
 * Nicht jede Verschiebung ist eine Zahl: reicht das Geld in der Basis ueber den
 * Horizont, im Szenario aber nicht mehr, gibt es keine Differenz zu nennen.
 */
function monatsDelta(basis: number | null, szenario: number | null): Delta {
  if (basis === null && szenario === null) return undefined;
  if (basis === null && szenario !== null) {
    return { text: 'reißt die Runway auf', good: false };
  }
  if (basis !== null && szenario === null) {
    return { text: 'trägt über den Horizont', good: true };
  }

  const differenz = (szenario as number) - (basis as number);
  if (differenz === 0) return undefined;

  const monate = Math.abs(differenz) === 1 ? 'Monat' : 'Monate';
  return {
    text: `${differenz > 0 ? '+' : '−'}${Math.abs(differenz)} ${monate}`,
    good: differenz > 0,
  };
}

/** Veraenderung des Tiefpunkts — mit dem Vorzeichen im Text, nicht davor. */
function euroDelta(basis: string, szenario: string): Delta {
  const differenz = Number(szenario) - Number(basis);
  if (Math.round(differenz) === 0) return undefined;
  return {
    text: `${differenz > 0 ? '+' : '−'}${formatEuro(Math.abs(differenz))}`,
    good: differenz > 0,
  };
}
